import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  CardBody,
  Button,
  Table,
  Badge,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import VoucherService from "../../../services/AccountingServices/VoucherService";
import toast from "react-hot-toast";

const STATUS_CONFIG = {
  NEW:       { color: "primary",  label: "New",      icon: "ri-file-line" },
  INITIATED: { color: "info",     label: "Initiated", icon: "ri-send-plane-2-line" },
  REVIEWED:  { color: "warning",  label: "Reviewed",  icon: "ri-search-eye-line" },
  APPROVED:  { color: "success",  label: "Approved",  icon: "ri-checkbox-circle-line" },
  REJECTED:  { color: "danger",   label: "Rejected",  icon: "ri-close-circle-line" },
};

const TYPE_CONFIG = {
  JOURNAL: { color: "primary" },
  INCOME:  { color: "success" },
  PAYMENT: { color: "warning" },
};

const VoucherDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [voucher, setVoucher] = useState(null);

  useEffect(() => {
    if (id) {
      fetchVoucher();
    }
  }, [id]);

  const fetchVoucher = async () => {
    try {
      setLoading(true);
      const response = await VoucherService.getById(id);
      setVoucher(response);
    } catch (error) {
      console.error("Error fetching voucher detail:", error);
      toast.error("Failed to load voucher details");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (action, label) => {
    try {
      setActionLoading(action);
      await VoucherService[action](id);
      toast.success(`Voucher ${label} successfully`);
      fetchVoucher();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${label.toLowerCase()} voucher`);
    } finally {
      setActionLoading(null);
    }
  };
const breadcrumbItems = [{ title: "Back To List", link: "/vouchers" }];

  const statusCfg = voucher ? (STATUS_CONFIG[voucher.status] || { color: "secondary", label: voucher.status }) : {};
  const typeCfg = voucher ? (TYPE_CONFIG[voucher.voucherType] || { color: "info" }) : { color: "info" };
  const totalDr = voucher?.details?.reduce((s, d) => s + (d.drAmount || 0), 0) || 0;
  const totalCr = voucher?.details?.reduce((s, d) => s + (d.crAmount || 0), 0) || 0;

  return (
    <Container fluid>

    <div className="page-content">
      
        <BreadCrumb title="Voucher Details" pageTitle="Accounting" breadcrumbItems={breadcrumbItems} />
        
        <Row className="mb-4">
          <Col lg={12}>
            <div className="d-flex align-items-center justify-content-between">
              
              
              <div className="d-flex gap-2">
                {voucher?.status === "NEW" && (
                  <Button
                    color="primary"
                    className="d-flex align-items-center gap-1 px-3"
                    onClick={() => handleStatusChange("initiate", "Initiated")}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === "initiate" ? <Spinner size="sm" /> : <i className="ri-send-plane-2-line"></i>}
                    Initiate
                  </Button>
                )}
                {voucher?.status === "INITIATED" && (
                  <Button
                    color="info"
                    className="d-flex align-items-center gap-1 px-3"
                    onClick={() => handleStatusChange("review", "Reviewed")}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === "review" ? <Spinner size="sm" /> : <i className="ri-search-eye-line"></i>}
                    Mark as Reviewed
                  </Button>
                )}
                {voucher?.status === "REVIEWED" && (
                  <>
                    <Button
                      color="success"
                      className="d-flex align-items-center gap-1 px-3"
                      onClick={() => handleStatusChange("approve", "Approved")}
                      disabled={!!actionLoading}
                    >
                      {actionLoading === "approve" ? <Spinner size="sm" /> : <i className="ri-checkbox-circle-line"></i>}
                      Approve
                    </Button>
                    <Button
                      color="danger"
                      className="d-flex align-items-center gap-1 px-3"
                      onClick={() => handleStatusChange("reject", "Rejected")}
                      disabled={!!actionLoading}
                    >
                      {actionLoading === "reject" ? <Spinner size="sm" /> : <i className="ri-close-circle-line"></i>}
                      Reject
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <div className="mt-2 text-muted small">Loading voucher details...</div>
          </div>
        ) : voucher ? (
          <Row justify-content-center>
            <Col lg={12} className="mx-auto">
              {/* ── Voucher Header Card ── */}
              <Card className="shadow-lg border-0 mb-4 overflow-hidden">
                <div 
                  className={`card-header p-4 d-flex justify-content-between align-items-center border-0`}
                  style={{ background: `var(--vz-${statusCfg.color}-bg, #e9f3fe)` }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div>
                      <h4 className="mb-1 fw-bold text-dark">{voucher.voucherSn}</h4>
                      <div className="d-flex align-items-center gap-2">
                        <Badge color={typeCfg.color} className="text-uppercase fw-bold shadow-sm px-2 py-1" style={{ minWidth: "80px" }}>{voucher.voucherType}</Badge>
                        <Badge color="light" className="text-muted border fw-normal">{voucher.fiscalYearName || voucher.fiscalYearCode}</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="text-end">
                    <Badge
                      color={statusCfg.color}
                      className="text-uppercase fw-bold px-1 py-1 fs-13 shadow-sm"
                      style={{ letterSpacing: "0.05em", minWidth: "100px" }}
                    >
                      {statusCfg.label}
                    </Badge>
                  </div>
                </div>
                
                <CardBody className="p-4 bg-white">
                  <Row className="gy-4 mb-4">
                    <Col sm={4}>
                      <div className="fw-semibold small text-muted text-uppercase mb-1" style={{ letterSpacing: '0.05em' }}>Transaction Date</div>
                      <div className="fw-bold fs-15 text-dark">
                        <i className="ri-calendar-event-line me-2 text-primary"></i>
                        {new Date(voucher.valueDate).toLocaleDateString("en-US", {
                          year: "numeric", month: "long", day: "numeric",
                        })}
                      </div>
                    </Col>
                    <Col sm={4}>
                      <div className="fw-semibold small text-muted text-uppercase mb-1" style={{ letterSpacing: '0.05em' }}>Fiscal Year</div>
                      <div className="fw-bold fs-15 text-dark">{voucher.fiscalYearName || voucher.fiscalYearCode}</div>
                    </Col>
                    <Col sm={4}>
                      <div className="fw-semibold small text-muted text-uppercase mb-1" style={{ letterSpacing: '0.05em' }}>Total Voucher Amount</div>
                      <div className="fw-bold fs-15 text-dark">
                        <span className="text-success me-1">DR</span> {totalDr.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className="fw-semibold small text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>Description / Narration</div>
                      <div 
                        className="p-3 rounded-2 text-dark bg-light border-start border-4 border-primary"
                        style={{ fontSize: '0.92rem', minHeight: '60px' }}
                      >
                        {voucher.description || "No narration provided."}
                      </div>
                    </Col>
                  </Row>

                  {/* ── Entries Table ── */}
                  <div className="table-responsive rounded-2 overflow-hidden border shadow-sm">
                    <Table className="mb-0 align-middle" hover>
                      <thead className="table-light">
                        <tr className="text-muted small text-uppercase">
                          <th width="45%" className="px-4 py-3 fw-bold" style={{ letterSpacing: "0.06em" }}>Ledger Account Details</th>
                          <th width="20%" className="text-end px-4 py-3 fw-bold" style={{ letterSpacing: "0.06em" }}>Debit (DR)</th>
                          <th width="20%" className="text-end px-4 py-3 fw-bold" style={{ letterSpacing: "0.06em" }}>Credit (CR)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {voucher.details?.map((detail, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-3 border-bottom">
                              <div className="fw-bold text-primary fs-14">{detail.ledgerName}</div>
                              <div className="small text-muted">{detail.ledgerCode}</div>
                              {detail.remarks && (
                                <div className="text-muted fst-italic mt-1 small">
                                  <i className="ri-edit-2-line me-1"></i> {detail.remarks}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-end border-bottom">
                              {detail.drAmount > 0 ? (
                                <span className="fw-bold text-success fs-14">
                                  {detail.drAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-end border-bottom">
                              {detail.crAmount > 0 ? (
                                <span className="fw-bold text-danger fs-14">
                                  {detail.crAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-light fw-bold text-end">
                        <tr>
                          <td className="px-4 py-3 text-start small text-uppercase text-muted" style={{ letterSpacing: "0.05em" }}>Total Amount</td>
                          <td className="px-4 py-3 text-end text-success fs-15 border-top border-2">
                             {totalDr.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3 text-end text-danger fs-15 border-top border-2">
                             {totalCr.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>

                  <div className="d-flex justify-content-end mt-4">
                    {Math.abs(totalDr - totalCr) <= 0.01 && totalDr > 0 ? (
                      <Badge color="soft-success" className="px-4 py-2 fs-12 rounded-pill">
                        <i className="ri-checkbox-circle-fill me-1 fs-15 align-middle"></i> Document is Balanced
                      </Badge>
                    ) : (
                      <Badge color="soft-danger" className="px-4 py-2 fs-12 rounded-pill">
                        <i className="ri-error-warning-fill me-1 fs-15 align-middle"></i> Document is Unbalanced
                      </Badge>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : null}
      
    </div>
    </Container>
  );
};

export default VoucherDetail;
